import { gql, useMutation, useQuery } from '@apollo/client';
import React, { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Form, { Field, Submit } from '../components/Form';
import LoadingIndicator from '../components/LoadingIndicator';
import { ACTION_INVITE_ACCEPT, CATEGORY_USER } from '../constants/analytics';
import type {
  AcceptInvitationMutation,
  AcceptInvitationMutationVariables,
  InvitationQuery,
  InvitationQueryVariables,
} from '../graphql/generated/graphqlTypes';
import usePublicOrganization from '../organization/usePublicOrganization';
import UIActions from '../ui/UIActions';
import { track } from '../utils/analytics';
import { Link, useRouter } from '../utils/router/v3Bridge';
import AccountContentContainer from './AccountContentContainer';
import canAuthenticateWithPassword from './canAuthenticateWithPassword';
import styles from './InvitationView.module.scss';
import IntegrationLoginButtons from './login/IntegrationLoginButtons';
import LoginHandler from './LoginHandler';
import usePostLoginHandler from './usePostLoginHandler';
import { useAppDispatch } from '../app/redux/useAppSelector';

const INVITATION_QUERY = gql`
  query invitation($id: ID!) {
    invitation(id: $id) {
      id
      email
      name
      acceptedAt
      type
      provision {
        id
      }
    }
  }
`;

const ACCEPT_INVITATION_MUTATION = gql`
  mutation acceptInvitation($id: ID!, $password: String!, $name: String!) {
    session: acceptInvitation(id: $id, password: $password, name: $name) {
      authToken
    }
  }
`;

interface InvitationValues {
  email: string;
  name: string;
  password: string;
}

const InvitationView = () => {
  const router = useRouter();
  const params = useParams() as { id: string };
  const dispatch = useAppDispatch();

  const organization = usePublicOrganization();

  const { data, loading } = useQuery<InvitationQuery, InvitationQueryVariables>(
    INVITATION_QUERY,
    { variables: { id: params.id } },
  );

  const [acceptInvitation] = useMutation<
    AcceptInvitationMutation,
    AcceptInvitationMutationVariables
  >(ACCEPT_INVITATION_MUTATION);

  const invitation = data?.invitation;

  const handleAcceptedError = useCallback(() => {
    router.push('/login');
    dispatch(
      UIActions.pushToast(
        {
          level: 'error',
          message: 'Invitation already accepted. Please login normally.',
        },
        3000,
      ),
    );
  }, [router, dispatch]);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!invitation) {
      router.push('/login');
      dispatch(
        UIActions.pushToast(
          {
            level: 'error',
            message:
              'Oops, we could not find your invitation. Please check the URL or ask your teammate to re-send an invitation.',
          },
          10000,
        ),
      );
    } else if (invitation.acceptedAt) {
      handleAcceptedError();
    } else if (!canAuthenticateWithPassword(organization, invitation.type)) {
      router.push('/login');
      dispatch(
        UIActions.pushToast(
          {
            level: 'warning',
            message: `Please use Single Sign-On to join ${organization.name}.`,
          },
          3000,
        ),
      );
    }
  }, [
    loading,
    invitation,
    organization,
    router,
    dispatch,
    handleAcceptedError,
  ]);

  const postLoginHandler = usePostLoginHandler();

  const onSubmit = async (values: InvitationValues) => {
    const { data: acceptedInvitationData } = await acceptInvitation({
      variables: {
        id: params.id,
        password: values.password,
        name: values.name,
      },
    });
    track(ACTION_INVITE_ACCEPT, { category: CATEGORY_USER });
    if (acceptedInvitationData?.session) {
      postLoginHandler(acceptedInvitationData.session.authToken);
    }
  };

  const handleSeatLimitError = () => {
    router.push('/login');
    dispatch(
      UIActions.pushToast(
        {
          level: 'error',
          message:
            'Your team has reached its seat limit. Please upgrade or contact support.',
        },
        3000,
      ),
    );
  };

  const { ssoPlatforms } = organization;

  return (
    <AccountContentContainer
      title={`Welcome to ${organization.name}`}
      subtitle="Let's set up your account!"
    >
      <LoginHandler>
        {loading || !invitation ? (
          <div className={styles.loadingWrapper}>
            <LoadingIndicator />
          </div>
        ) : (
          <>
            <Form<InvitationValues>
              apiErrorHandlers={{
                TOKEN_REUSE: handleAcceptedError,
                SEAT_LIMIT_MAXED: handleSeatLimitError,
              }}
              onSubmit={onSubmit}
              initialValues={{
                email: invitation.email ?? '',
                name: invitation.name ?? '',
                password: '',
              }}
            >
              <Field
                required
                label="Your full name"
                type="text"
                name="name"
                autoComplete="name"
              />
              <Field
                disabled
                type="email"
                label="Email"
                name="email"
                autoComplete="username email"
              />
              <Field
                required
                label="Password"
                type="password"
                name="password"
                autoComplete="new-password"
              />
              <Submit block>Sign up</Submit>
              <div className="login-link">
                Already have an account? <Link to="/login">Login</Link>
              </div>
            </Form>
            {ssoPlatforms.length > 0 && (
              <>
                <div className="auth-divider" />
                <IntegrationLoginButtons
                  email={organization.email}
                  ssoPlatforms={ssoPlatforms}
                  register
                />
              </>
            )}
          </>
        )}
      </LoginHandler>
    </AccountContentContainer>
  );
  // }
};

export default InvitationView;
